
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FontSizeControl } from '@/components/FontSizeControl';
import { Separator } from '@/components/ui/separator';
import { Upload, Settings, Type, Image } from 'lucide-react';
import { toast } from 'sonner';

export const SiteSettings = () => {
  const [siteTitle, setSiteTitle] = useState('نظام السياحة');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    const savedTitle = localStorage.getItem('site-title');
    const savedFavicon = localStorage.getItem('site-favicon');
    const savedLogo = localStorage.getItem('site-logo');
    
    if (savedTitle) setSiteTitle(savedTitle);
    if (savedFavicon) setFaviconUrl(savedFavicon);
    if (savedLogo) setLogoUrl(savedLogo);
  }, []);

  const updateSiteTitle = () => {
    localStorage.setItem('site-title', siteTitle);
    toast.success("تم تحديث عنوان الموقع بنجاح");
    window.dispatchEvent(new Event('storage'));
    setTimeout(() => window.location.reload(), 1000);
  };

  const updateFavicon = () => {
    if (!faviconUrl.trim()) {
      toast.error("يرجى إدخال رابط أيقونة صحيح");
      return;
    }

    localStorage.setItem('site-favicon', faviconUrl);
    
    // تحديث favicon في الصفحة مباشرة
    const existingLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (existingLink) {
      existingLink.href = faviconUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrl;
      document.head.appendChild(link);
    }
    
    toast.success("تم تحديث أيقونة الموقع بنجاح");
  };

  const updateLogo = () => {
    if (!logoUrl.trim()) {
      toast.error("يرجى إدخال رابط شعار صحيح");
      return;
    }

    localStorage.setItem('site-logo', logoUrl);
    toast.success("تم تحديث شعار النظام بنجاح. سيظهر التحديث عند إعادة تحميل الصفحة");
    window.dispatchEvent(new Event('storage'));
    setTimeout(() => window.location.reload(), 1000);
  };

  return (
    <div className="space-y-6">
      {/* إعدادات العنوان */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-primary/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary to-primary-hover shadow-lg shadow-primary/30">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">عنوان الموقع</span>
          </CardTitle>
          <CardDescription className="text-base">
            تخصيص عنوان الموقع الذي يظهر في رأس الصفحة والشريط الجانبي
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-2">
            <Label htmlFor="site-title" className="text-base font-medium">عنوان الموقع</Label>
            <div className="flex gap-2">
              <Input
                id="site-title"
                value={siteTitle}
                onChange={(e) => setSiteTitle(e.target.value)}
                placeholder="أدخل عنوان الموقع"
                className="flex-1 border-border/50 focus:ring-2 focus:ring-primary/30 transition-all"
              />
              <Button onClick={updateSiteTitle} className="bg-gradient-to-r from-primary to-primary-hover hover:shadow-lg hover:scale-105 transition-all">
                حفظ
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* إعدادات الخط */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-appearance))]/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-appearance))] to-[hsl(var(--tab-appearance))]/80 shadow-lg shadow-[hsl(var(--tab-appearance))]/30">
              <Type className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">حجم الخط</span>
          </CardTitle>
          <CardDescription className="text-base">
            تحكم في حجم الخط لكامل النظام
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center justify-center mb-6">
            <FontSizeControl />
          </div>
          <div className="text-center p-4 bg-info/10 rounded-xl border border-info/20">
            <p className="text-sm text-foreground/80">
              يؤثر تغيير حجم الخط على جميع النصوص في النظام بما في ذلك القائمة الرئيسية
            </p>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* إعدادات أيقونة الموقع */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-success/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-success to-success/80 shadow-lg shadow-success/30">
              <Image className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">أيقونة الموقع (Favicon)</span>
          </CardTitle>
          <CardDescription className="text-base">
            أيقونة الموقع التي تظهر في تبويب المتصفح
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-2">
            <Label htmlFor="favicon-url" className="text-base font-medium">رابط الأيقونة</Label>
            <div className="flex gap-2">
              <Input
                id="favicon-url"
                value={faviconUrl}
                onChange={(e) => setFaviconUrl(e.target.value)}
                placeholder="https://example.com/favicon.png"
                className="flex-1 border-border/50 focus:ring-2 focus:ring-success/30 transition-all"
                type="url"
              />
              <Button onClick={updateFavicon} className="bg-gradient-to-r from-success to-success/80 hover:shadow-lg hover:scale-105 transition-all">
                تحديث
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              استخدم رابط مباشر لصورة PNG أو ICO بحجم 32x32 بكسل أو أكبر
            </p>
          </div>
        </CardContent>
      </Card>

      {/* إعدادات شعار النظام */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-warning/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-warning to-warning/80 shadow-lg shadow-warning/30">
              <Upload className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">شعار النظام</span>
          </CardTitle>
          <CardDescription className="text-base">
            الشعار الذي يظهر بجانب عنوان الموقع في رأس الصفحة
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <div className="space-y-2">
            <Label htmlFor="logo-url" className="text-base font-medium">رابط الشعار</Label>
            <div className="flex gap-2">
              <Input
                id="logo-url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="flex-1 border-border/50 focus:ring-2 focus:ring-warning/30 transition-all"
                type="url"
              />
              <Button onClick={updateLogo} className="bg-gradient-to-r from-warning to-warning/80 hover:shadow-lg hover:scale-105 transition-all">
                تحديث
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              استخدم رابط مباشر لصورة PNG أو JPG. الحجم المثالي 40x40 بكسل
            </p>
          </div>
          
          {logoUrl && (
            <div className="mt-4 p-4 bg-gradient-to-br from-muted/20 to-muted/10 rounded-xl border border-border/50">
              <Label className="text-base font-medium mb-3 block">معاينة الشعار:</Label>
              <div className="mt-2 p-4 border border-border/30 rounded-lg bg-background/50 backdrop-blur-sm">
                <img 
                  src={logoUrl} 
                  alt="شعار النظام" 
                  className="h-10 w-10 object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="p-5 bg-gradient-to-br from-info/10 to-info/5 border border-info/20 rounded-xl shadow-md">
        <p className="text-sm text-foreground/80 font-medium flex items-start gap-2">
          <span className="text-info text-lg">💡</span>
          <span>نصيحة: تأكد من أن الروابط التي تستخدمها متاحة عبر الإنترنت ولا تتطلب تسجيل دخول</span>
        </p>
      </div>
    </div>
  );
};
